import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CoWorkingNavBar from './CoWorkingLandingNavBar';
import Pagination from './Pagination';
import { ArrowRightCircleIcon } from "@heroicons/react/24/outline";
import { Link, useNavigate } from 'react-router-dom';
import { fetchDataFromMicroCMS } from '../utils/microCMSApi';

export interface NewsItem {
    id: string;
    createdAt: string;
    title_en: string;
    title_ja: string;
    content_en: string;
    content_ja: string;
    publishedAt: string;
    tags: { fieldId: string; tag: string }[];
}

interface NewsResponse {
    contents: NewsItem[];
    totalCount: number;
}

interface CoWorkingNewsListProps {
    showButton: boolean;
    addMargin: boolean;
}

const CoWorkingNewsList: React.FC<CoWorkingNewsListProps> = ({ showButton = false, addMargin = true }) => {
    const navigate = useNavigate();
    const [news, setNews] = useState<NewsItem[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const itemsPerPage = 10;
    const { t, i18n } = useTranslation();

    useEffect(() => {
        const fetchNewsData = async () => {
            setLoading(true);
            try {
                const isProduction = process.env.REACT_APP_ENV === 'production' || process.env.REACT_APP_ENV === 'Production';
                const endpoint = `news?orders=-publishedAt&limit=${itemsPerPage}&offset=${(currentPage - 1) * itemsPerPage}${isProduction ? `&filters=public[equals]true` : ''}`;
                const data = await fetchDataFromMicroCMS(endpoint) as NewsResponse;
                setNews(data.contents);
                setTotalPages(Math.ceil(data.totalCount / itemsPerPage));
            } catch (error) {
                console.error('Error fetching news:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchNewsData();
    }, [currentPage]);

    const truncateText = (htmlString: string, maxLength: number) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlString || '';
        const plainText = tempDiv.innerText.replace(/\n+/g, ' ').replace(/\s+/g, ' ').trim();
        return plainText.length > maxLength ? plainText.slice(0, maxLength) + '...' : plainText;
    };

    const handleNewsClick = (newsItem: NewsItem) => {
        navigate(`/news/${newsItem.id}`);
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage((prev) => prev + 1);
        }
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage((prev) => prev - 1);
        }
    };

    const currentLang = i18n.language;
    const getTitle = (item: NewsItem) => (currentLang === 'ja' ? item.title_ja : item.title_en);
    const getContent = (item: NewsItem) => (currentLang === 'ja' ? item.content_ja : item.content_en);

    return (
        <div className="w-full text-[#2C2E35]">
            <CoWorkingNavBar />

            <div className={`w-full px-4 md:px-0 pt-0 mb-10 md:max-w-5xl lg:max-w-6xl mx-auto ${addMargin ? 'pt-[85px]' : '-mt-[3rem]'} newTitle`} id="news">
                <div className="relative mb-4">
                    <h1 className="text-5xl font-bold leading-[1.1] text-center">{t('cw_lp_common.news')}</h1>
                </div>
                <div className="w-full py-2">
                    {loading ? (
                        <p className="text-center">{t('cw_lp_common.loading')}</p>
                    ) : news.length === 0 ? (
                        <p className="text-center">{t('cw_lp_news_1')}</p>
                    ) : (
                        <div className="flex flex-col items-end w-full">
                            {news.slice(0, showButton ? 3 : news.length).map((item) => (
                                <div
                                    key={item.id}
                                    className="flex flex-col sm:flex-row sm:items-start w-full cursor-pointer border-b border-gray-300 py-5 sm:py-6 gap-2 sm:gap-4"
                                    onClick={() => handleNewsClick(item)}
                                >
                                    <div className="w-32 text-sm text-gray-600 text-left flex-shrink-0">
                                        {new Date(item.publishedAt).toLocaleDateString()}
                                    </div>

                                    <div className="flex flex-col flex-1">
                                        <p className="font-bold text-lg leading-snug mb-1">{getTitle(item)}</p>
                                        <p className="text-gray-700 text-sm">
                                            {truncateText(getContent(item), 150)}
                                        </p>
                                    </div>
                                </div>
                            ))}

                            {totalPages > 1 && !showButton && (
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onNext={handleNextPage}
                                    onPrevious={handlePreviousPage}
                                />
                            )}

                            {showButton && (
                                <div className="relative z-20 w-full flex sm:justify-end mb-4 sm:mb-0">
                                    <button className="sm:relative absolute left-1/2 transform -translate-x-1/2 sm:left-auto sm:transform-none px-4 py-2 rounded w-[200px] hover:text-gray-400">
                                        <Link to="/news" className="flex items-center justify-center">
                                            <span className="mr-1">{t('cw_lp_home_7')}</span>
                                            <ArrowRightCircleIcon className="w-8 h-8" />
                                        </Link>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CoWorkingNewsList;
